import {
    getActionEndpoint,
    isDisposablePreSubscriptionAction,
    isImmediateAction,
    isSubscriptionRequiredAction,
    isWriteAheadAction,
    TrackedEventAction,
} from './tracked-event-action'
import { TrackedEvent, TrackedEventUtils } from './tracked-event'
import { type Logger, TaggedLogger } from '../logging/logger'
import { Globals } from '../globals'
import { Result } from '../models/result'
import { httpStatusOf, Request } from '../request/request'
import { isRetryable } from '../request/backoff'
import { ensureError } from '../utils/ensure-error'
import { RequestMethod } from '../request/types'
import { getObjectWithoutNullOrUndefined } from '../utils/object'
import { SubscriptionToken } from '../types/subscription-token.ts'
import { PNException } from '../exceptions/pn-exception.ts'

/** @internal */
export interface EventManagerAsyncProvider {
    _canSend?: () => Promise<boolean>
    _getIsSubscribed?: () => Promise<boolean>
    _getAnonymousId?: () => Promise<string>
    _getToken?: () => Promise<SubscriptionToken | null>
    _getDomainId?: () => Promise<number | null>
    _getCachedEvents?: () => Promise<TrackedEvent[]>
    _setCachedEvents?: (events: TrackedEvent[]) => Promise<TrackedEvent[]>
    _getWriteAheadEvents?: () => Promise<TrackedEvent[]>
    _addWriteAheadEvent?: (event: TrackedEvent) => Promise<boolean>
    _removeWriteAheadEvent?: (eventId: string) => Promise<void>
}

/** @internal */
export type EventTracker = {
    _track(error: Error): void
    _track(error: Error, context: any): void
    _track(event: TrackedEvent): void
    _track(action: TrackedEventAction): void
    _track(action: TrackedEventAction, data: Record<string, any>): void
    _trackAsync(error: Error): Promise<void>
    _trackAsync(event: TrackedEvent): Promise<void>
    _trackAsync(action: TrackedEventAction): Promise<void>
    _trackAsync(action: TrackedEventAction, data: Record<string, any>): Promise<void>
    _trackWriteAhead(event: TrackedEvent): Promise<boolean>
    _sendPendingWriteAheadEvents(): Promise<void>
    _discardQueuedEvents(): Promise<void>
}

/**
 * Whether a write-ahead event whose delivery ended with `httpStatus` should be kept for the next
 * load. Any 2xx means the server received it, whatever the body. 401 and 403 are kept even though
 * the request policy treats them as permanent: an edge or firewall can return them transiently,
 * and losing a withdrawal costs more than one request per page load.
 *
 * @internal
 */
export function shouldKeepWriteAheadEvent(httpStatus: number | undefined): boolean {
    if (httpStatus === undefined) {
        return true
    }

    if (httpStatus >= 200 && httpStatus < 300) {
        return false
    }

    if (httpStatus === 401 || httpStatus === 403) {
        return true
    }

    return isRetryable(httpStatus)
}

/** @internal */
export class EventManager implements EventTracker {
    private _logger: Logger
    protected _eventQueue: TrackedEvent[]
    protected _trackedEventQueueTimer?: any // Timeout
    protected _isEventQueueFlushing: boolean

    public constructor(
        private asyncProvider: EventManagerAsyncProvider = {},
        logger?: Logger,
    ) {
        this._logger = logger ?? TaggedLogger._getInstance('EventManager')
        this._eventQueue = []
        this._isEventQueueFlushing = false

        this._setup()
    }

    public _setup() {
        this._hydrateEventQueue().then(() => this._startEventQueueTimer())

        // Every page that boots the bundle retries, not only one that calls `load`: an integration
        // can hold `load` back indefinitely, for example until consent is given.
        this._sendPendingWriteAheadEvents().catch((err) => this._logger.error(err))
    }

    public async _canSend(): Promise<boolean> {
        return this.asyncProvider._canSend?.() ?? Promise.resolve(true)
    }

    public async _isSubscribed(): Promise<boolean> {
        return this.asyncProvider._getIsSubscribed ? await this.asyncProvider._getIsSubscribed() : false
    }

    public _track(error: Error): void
    public _track(error: Error, context: any): void
    public _track(event: TrackedEvent): void
    public _track(action: TrackedEventAction): void
    public _track(action: TrackedEventAction, data: Record<string, any>): void
    public _track(actionOrEventOrError: TrackedEventAction | TrackedEvent | Error, data: any = {}): void {
        // Do not use "await" when invoking internal _trackEvent
        // Event requests should be pushed into the EventLoop to
        // prevent any client page blocking
        this._trackAsync(actionOrEventOrError as any, data).then()
    }

    public async _trackAsync(error: Error): Promise<void>
    public async _trackAsync(error: Error, context: any): Promise<void>
    public async _trackAsync(event: TrackedEvent): Promise<void>
    public async _trackAsync(action: TrackedEventAction): Promise<void>
    public async _trackAsync(action: TrackedEventAction, data: Record<string, any>): Promise<void>
    public async _trackAsync(
        actionOrEventOrError: TrackedEventAction | TrackedEvent | Error,
        data: any = {},
    ): Promise<void> {
        try {
            let event: TrackedEvent
            if (actionOrEventOrError instanceof Error) {
                // ERROR

                if (PNException._isSuppressed(actionOrEventOrError)) {
                    // Muted errors are not streamed
                    return
                }

                event = await TrackedEventUtils._getErrorEvent(actionOrEventOrError)
                event.data.logs = this._logger._getLogs(true).splice(-10).reverse()

                if (data) {
                    event.data[Globals._tevFieldErrorContext] = data
                }
            } else if (typeof actionOrEventOrError === 'string') {
                // TRACKED_EVENT_ACTION
                event = await TrackedEventUtils._build(actionOrEventOrError, data)
            } else {
                // TRACKED_EVENT
                event = actionOrEventOrError
            }

            return await this._trackEvent(event)
        } catch (err) {
            this._logger.error(err)
        }
    }

    /**
     * Stores `event` so a later load can retry it, then sends it in the background, regardless of
     * {@link _canSend}. Resolves once the event is stored, so a caller can sequence what follows
     * the store without waiting on the network, whose retries can take seconds.
     *
     * @returns whether the event was stored durably.
     */
    public async _trackWriteAhead(event: TrackedEvent): Promise<boolean> {
        const stored = await this._storeWriteAheadEvent(event)
        void this._deliverWriteAheadEvent(event)
        return stored
    }

    /**
     * Makes one delivery attempt per stored write-ahead event per page load, from {@link _setup}.
     * Attempting once per load, rather than on the queue timer, bounds a browser that is offline or
     * being refused; each attempt can itself retry briefly within the request's own short retry
     * budget.
     */
    public async _sendPendingWriteAheadEvents(): Promise<void> {
        let pending: TrackedEvent[] = []
        try {
            pending = (await this.asyncProvider._getWriteAheadEvents?.()) ?? []
        } catch (err) {
            this._logger.error(err)
        }

        // One at a time: each removal rewrites the single stored list, so concurrent removals
        // could overwrite one another and resurrect a delivered event.
        for (const event of pending) {
            await this._deliverWriteAheadEvent(event)
        }
    }

    protected async _storeWriteAheadEvent(event: TrackedEvent): Promise<boolean> {
        if (!this.asyncProvider._addWriteAheadEvent) {
            return false
        }

        try {
            const stored = await this.asyncProvider._addWriteAheadEvent(event)
            if (!stored) {
                this._logger.critical(`Unable to store ${event.action} ${event.event_id}; it will not be retried`)
            }
            return stored
        } catch (err) {
            this._logger.critical(err)
            return false
        }
    }

    /**
     * Sends without the late-init lookups of {@link _process}, so this is safe at boot. Those
     * lookups read, and when nothing is stored save, an anonymous id, domain id and token, and before
     * `load` sets the domain key and storage scope they would save default values across the whole
     * domain. A write-ahead event needs none of them: it was complete when stored, and it keeps the
     * token it was made against.
     */
    protected async _deliverWriteAheadEvent(event: TrackedEvent): Promise<void> {
        let res: Result<boolean>
        try {
            res = await this._prepareAndSendEvents([event], Globals._kApiEventsUrl)
        } catch (err) {
            this._logger.error(err)
            return
        }

        if (res.isErr() && shouldKeepWriteAheadEvent(httpStatusOf(res.getErr()))) {
            this._logger.verbose(`Keeping ${event.action} ${event.event_id} for the next load`)
            return
        }

        try {
            await this.asyncProvider._removeWriteAheadEvent?.(event.event_id)
        } catch (err) {
            this._logger.error(err)
        }
    }

    protected async _trackEvent(event: TrackedEvent): Promise<void> {
        // Write-ahead events bypass the batch queue entirely: it is gated on the halt state they
        // exist to report, and its storage is overwritten whole by every tab.
        if (isWriteAheadAction(event.action)) {
            await this._trackWriteAhead(event)
            return
        }

        if (this._eventQueue.length >= Globals._eventQueueMaxSize) {
            this._eventQueue = this._eventQueue.slice(0, Globals._eventQueueMaxSize - 1)
            await this._persistEventQueue()
        }

        if (this._eventQueue.find((ev) => ev.event_id === event.event_id)) {
            this._logger.debug(`Event ${event.event_id} has already been added to the queue`)
            return
        }

        if (this._eventQueue.find((ev) => TrackedEventUtils._isEqual(ev, event))) {
            this._logger.debug(`Event ${event.event_id} is a duplicate event`)
            return
        }

        // Certain subscription required events are dropped prior to subscription
        // to prevent out-of-date or out-of-scope events from being sent in the
        // post-sub batch.
        const isSubscribed = await this._isSubscribed()
        if (!isSubscribed && isDisposablePreSubscriptionAction(event.action)) {
            this._logger.debug(`Ignoring event ${event.event_id} (${event.action}) - user is not subscribed.`)
            return
        }

        // Dropped rather than queued: a queue collected while halted would be released by a later
        // opt-in, sending events the visitor had withdrawn consent for.
        if (!(await this._canSend())) {
            this._logger.debug(`Dropping event ${event.event_id} (${event.action}) - tracking is halted.`)
            return
        }

        if (!isImmediateAction(event.action)) {
            this._logger.debug(`Queueing event ${event.event_id} (${event.action})`)
            this._eventQueue.push(event)
            await this._persistEventQueue()
            return
        }

        this._logger.debug(`Processing event ${event.event_id} (${event.action})`)
        const res = await this._process([event], getActionEndpoint(event.action))

        if (res.isErr()) {
            this._logger.verbose(`Requeueing event ${event.event_id} (${event.action})`)
            this._eventQueue.unshift(event)
        }
    }

    /**
     * Empties the batch queue, in memory and in storage, so that nothing collected before a
     * withdrawal of consent is sent if the visitor later opts back in.
     */
    public async _discardQueuedEvents(): Promise<void> {
        this._eventQueue = []
        await this._persistEventQueue()
    }

    protected async _hydrateEventQueue(): Promise<void> {
        if (!this.asyncProvider._getCachedEvents) {
            this._logger.verbose('Cache evaluator not set')
            return
        }

        const cached = await this.asyncProvider._getCachedEvents()
        if (!cached.length) {
            this._logger.verbose('Cache is empty')
            return
        }

        const eventsToHydrate = cached.filter((event) => {
            return this._eventQueue.findIndex((ev) => ev.event_id === event.event_id) === -1
        })

        this._eventQueue.push(...eventsToHydrate)

        if (eventsToHydrate.length) {
            this._logger.verbose(`Rehydrated ${eventsToHydrate.length} event(s) from cache`)
            await this._persistEventQueue()
        }
    }

    protected _startEventQueueTimer() {
        if (this._trackedEventQueueTimer) {
            this._logger.verbose('Queue timer already initialized')
            return
        }

        this._trackedEventQueueTimer = setTimeout(() => {
            this._flushEventQueue().then(() => {
                this._trackedEventQueueTimer = null
                this._startEventQueueTimer()
            })
        }, Globals._eventQueueFlushIntervalMS)
    }

    protected async _flushEventQueue(): Promise<void> {
        if (!this._eventQueue.length || this._isEventQueueFlushing || !(await this._canSend())) {
            return
        }

        await this._cleanupEventQueue()

        const batch = await this._getNextBatch()
        if (!batch.length) {
            this._setEventQueueFlushingState(false)
            return
        }

        // batched events should always use the default events URL
        const res = await this._process(batch, Globals._kApiEventsUrl)

        if (res.isErr()) {
            this._eventQueue.push(...batch)
            return
        }
    }

    /**
     * Flushes ALL currently-sendable queued events in a single request. Intended for
     * page-lifecycle hooks (visibilitychange:hidden / pagehide): the queue's interval
     * timer frequently does not fire before a user navigates away right after acting
     * on a prompt, so attribution-critical events (permission/prompt outcomes,
     * impressions) are otherwise lost. The request transport sets `keepalive`, so the
     * send survives unload. (NS-2558)
     */
    public async _flushPendingEvents(): Promise<void> {
        if (!this._eventQueue.length || !(await this._canSend())) {
            return
        }

        await this._cleanupEventQueue()

        const isSubscribed = await this._isSubscribed()
        const sendable: TrackedEvent[] = []
        const retained = this._eventQueue.filter((event) => {
            const canSend = isSubscribed || !isSubscriptionRequiredAction(event.action)
            if (canSend) {
                sendable.push(event)
            }
            return !canSend
        })

        if (!sendable.length) {
            return
        }

        this._eventQueue = retained
        await this._persistEventQueue()

        const res = await this._process(sendable, Globals._kApiEventsUrl)
        if (res.isErr()) {
            // Restore on failure so a subsequent flush / next session can retry.
            this._eventQueue.push(...sendable)
            await this._persistEventQueue()
        }
    }

    protected async _process(events: TrackedEvent[], url: string): Promise<Result<boolean>> {
        await this._preloadTrackedEventUtilProperties()
        return this._prepareAndSendEvents(events, url)
    }

    /**
     * Fills what each event lacks from the late-init values already in memory, then sends them in
     * one request. Performs no storage lookups.
     */
    protected async _prepareAndSendEvents(events: TrackedEvent[], url: string): Promise<Result<boolean>> {
        events.forEach((event) => {
            TrackedEventUtils._prepareSendEvent(event)
        })

        let jitterMillis: number | undefined
        const _events = events.map((event) => {
            const { sendAttemptCount, jxms, data, ...baseEvent } = event
            jitterMillis = jxms

            return Object.assign(
                getObjectWithoutNullOrUndefined(baseEvent as TrackedEvent),
                // Re-attach data ensuring null values are preserved
                { data },
            )
        })

        this._logger.verbose('Dispatching event processing')

        let req: Request | undefined
        try {
            let data = _events.length == 1 ? _events[0] : _events
            this._logger.verbose(`Building request data: ${data}`)

            req = new Request(RequestMethod.POST, url, data, { jitterMillis })

            this._logger.debug(`Prepared ${events.length} event(s):`, data)
        } catch (err) {
            const error = ensureError(err)
            this._logger.error(`Unable to prepare events: ${error.message}`)
            return Result.Err(error)
        }

        if (req) {
            const reqResult = await req._perform()

            if (reqResult.isErr()) {
                this._logger.error('Send failed;', reqResult.getErr()!.message)
                return reqResult
            } else {
                this._logger.verbose('Send successful')
                return Result.Ok(true)
            }
        } else {
            this._logger.verbose('No request to process')
            return Result.Ok(false)
        }
    }

    protected async _getNextBatch(): Promise<TrackedEvent[]> {
        let batchCount =
            this._eventQueue.length > Globals._eventQueueFlushTriggerSize
                ? Globals._eventQueueFlushTriggerSize
                : this._eventQueue.length

        if (!batchCount) {
            return []
        }

        const batch: TrackedEvent[] = []
        const isSubscribed = await this._isSubscribed()

        const nextEventQueue = this._eventQueue.filter((event) => {
            let canBatch = isSubscribed || !isSubscriptionRequiredAction(event.action)

            if (canBatch) {
                batch.push(event)
            }

            return !canBatch
        })

        if (batch.length) {
            this._eventQueue = nextEventQueue
            await this._persistEventQueue()
        }

        return batch
    }

    protected async _cleanupEventQueue(): Promise<void> {
        if (!this._eventQueue.length) {
            return
        }

        const cleanQueue = this._eventQueue.filter((event) => {
            return event.sendAttemptCount < Globals._eventMaxSendAttempts
        })

        if (cleanQueue.length !== this._eventQueue.length) {
            this._eventQueue = cleanQueue
            await this._persistEventQueue()
        }
    }

    protected _setEventQueueFlushingState(flushing: boolean) {
        this._isEventQueueFlushing = flushing
    }

    protected async _persistEventQueue(): Promise<void> {
        if (!this.asyncProvider._setCachedEvents) {
            return
        }

        // PushSubscription cannot be cloned/serialized to IDB
        this._eventQueue.forEach((event) => {
            if (event.token instanceof PushSubscription) {
                event.token = event.token.toJSON()
            }
        })

        return this.asyncProvider
            ._setCachedEvents(this._eventQueue)
            .catch((err) => this._logger.critical(err))
            .then()
    }

    protected async _preloadTrackedEventUtilProperties() {
        await Promise.all([
            !TrackedEventUtils._domainId
                ? this.asyncProvider._getDomainId?.()?.then((id) => {
                      TrackedEventUtils._domainId = id
                  })
                : null,

            !TrackedEventUtils._anonymousId
                ? this.asyncProvider._getAnonymousId?.()?.then((id) => {
                      TrackedEventUtils._anonymousId = id
                  })
                : null,

            !TrackedEventUtils._token
                ? this.asyncProvider._getToken?.()?.then((token) => {
                      if (token) {
                          TrackedEventUtils._token = token
                      }
                  })
                : null,
        ]).catch((err) => this._logger.warn(err))
    }
}
